import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import ReactMarkdown from "react-markdown";
import { extractCitations, ReferenceList } from "../lib/citations";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Microscope, Loader2, Trash2, ArrowLeft, Rocket, Lightbulb, Building2,
  GraduationCap, FlaskConical, Network, Sparkles,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: "landscape", label: "Informe de Paisaje" },
  { key: "technology", label: "Informe Tecnológico" },
  { key: "structuring", label: "Tendencias y Expertos" },
];

const MATURITY_COLORS = { emergente: "bg-amber-500/15 text-amber-600", crecimiento: "bg-sky-500/15 text-sky-600", madura: "bg-emerald-500/15 text-emerald-600" };
const IMPACT_COLORS = { alto: "bg-red-500/15 text-red-600", medio: "bg-amber-500/15 text-amber-600", bajo: "bg-slate-500/15 text-slate-500" };

function TechCard({ t, index }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = (t.description || "").length > 160;
  return (
    <Card><CardContent className="p-4">
      <div className="flex items-center justify-between mb-1 gap-2">
        <p className="font-semibold text-sm truncate min-w-0">{t.name}</p>
        {t.trl && <Badge variant="outline" className="text-[10px] flex-shrink-0">TRL {t.trl}</Badge>}
      </div>
      <p className={`text-xs text-muted-foreground leading-relaxed ${expanded ? "" : "line-clamp-4"}`}>{t.description}</p>
      {isLong && (
        <button
          type="button"
          className="text-[11px] font-medium text-emerald-600 hover:text-emerald-700 mt-1"
          onClick={() => setExpanded((v) => !v)}
          data-testid={`tech-toggle-${index}`}
        >
          {expanded ? "Ver menos" : "Ver más"}
        </button>
      )}
    </CardContent></Card>
  );
}

const md = {
  h2: (props) => <h2 className="text-lg font-bold mt-5 mb-2 text-emerald-700 dark:text-emerald-400" {...props} />,
  h3: (props) => <h3 className="text-base font-semibold mt-4 mb-1.5" {...props} />,
  p: (props) => <p className="text-sm leading-relaxed mb-2.5" {...props} />,
  ul: (props) => <ul className="list-disc pl-5 mb-3 space-y-1 text-sm" {...props} />,
  ol: (props) => <ol className="list-decimal pl-5 mb-3 space-y-1 text-sm" {...props} />,
  strong: (props) => <strong className="font-semibold" {...props} />,
  a: (props) => <a className="text-emerald-600 underline" target="_blank" rel="noreferrer" {...props} />,
};

export default function DeepResearchPage() {
  const { token } = useAuth();
  const headers = useCallback(() => ({ "Content-Type": "application/json", Authorization: `Bearer ${token}` }), [token]);
  const [reports, setReports] = useState([]);
  const [topic, setTopic] = useState("");
  const [focus, setFocus] = useState("");
  const [launching, setLaunching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [savingEco, setSavingEco] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/research`, { headers: headers() });
      const d = await r.json();
      setReports(d.reports || []);
      return d.reports || [];
    } catch { return []; }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  // Polling mientras haya investigaciones en curso
  useEffect(() => {
    const anyRunning = reports.some((r) => !["done", "error"].includes(r.status));
    if (anyRunning && !pollRef.current) {
      pollRef.current = setInterval(load, 4000);
    } else if (!anyRunning && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [reports, load]);

  const launch = async () => {
    if (!topic.trim()) return toast.error("Indica un tema de investigación");
    setLaunching(true);
    try {
      const r = await fetch(`${API}/research`, {
        method: "POST", headers: headers(),
        body: JSON.stringify({ topic, focus }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Error");
      toast.success("Investigación lanzada · los agentes IA están trabajando");
      setTopic(""); setFocus("");
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLaunching(false);
    }
  };

  const openReport = async (id) => {
    const r = await fetch(`${API}/research/${id}`, { headers: headers() });
    if (r.ok) setSelected(await r.json());
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    await fetch(`${API}/research/${id}`, { method: "DELETE", headers: headers() });
    setReports((prev) => prev.filter((r) => r.id !== id));
  };

  const addOrgsToEcosystem = async () => {
    const orgs = selected?.structured?.organizations || [];
    if (!orgs.length) return;
    setSavingEco(true);
    try {
      const r = await fetch(`${API}/ecosystem/entities/bulk`, {
        method: "POST", headers: headers(),
        body: JSON.stringify({
          entities: orgs.map((o) => ({
            name: o.name, entity_type: o.type, country: o.country,
            description: o.description, sector: selected.topic,
            why_relevant: `Identificada en Deep Research: "${selected.topic}"`,
          })),
        }),
      });
      const d = await r.json();
      toast.success(`${d.created} organizaciones añadidas al Ecosistema`);
    } catch {
      toast.error("Error guardando en el ecosistema");
    } finally {
      setSavingEco(false);
    }
  };

  // ---------- Vista detalle ----------
  if (selected) {
    const st = selected.structured || {};
    return (
      <div className="space-y-5" data-testid="research-detail">
        <Button variant="ghost" size="sm" onClick={() => setSelected(null)} data-testid="research-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver a investigaciones
        </Button>
        <div className="rounded-2xl p-6 md:p-8 text-white" style={{ background: "linear-gradient(120deg, #1e1b4b, #4338ca 70%, #6d28d9)" }}>
          <div className="flex items-center gap-2 text-indigo-200 text-xs font-semibold uppercase tracking-widest mb-2">
            <Microscope className="w-4 h-4" /> Deep Research
          </div>
          <h1 className="text-2xl md:text-3xl font-black">{selected.topic}</h1>
          {selected.focus && <p className="text-indigo-200 text-sm mt-1">Enfoque: {selected.focus}</p>}
          <p className="text-indigo-200/70 text-xs mt-2">Por {selected.created_by} · {new Date(selected.created_at).toLocaleDateString("es-ES")}</p>
        </div>

        {st.executive_summary && (
          <Card><CardContent className="p-5">
            <h2 className="font-bold text-sm uppercase tracking-wider text-muted-foreground mb-2">Resumen Ejecutivo</h2>
            <p className="text-sm leading-relaxed">{st.executive_summary}</p>
          </CardContent></Card>
        )}

        {(st.trends || []).length > 0 && (
          <div>
            <h2 className="font-bold text-base mb-3 flex items-center gap-2"><Lightbulb className="w-4 h-4 text-amber-500" /> Tendencias detectadas</h2>
            <div className="grid md:grid-cols-2 gap-3">
              {st.trends.map((t, i) => (
                <Card key={i} data-testid={`research-trend-${i}`}><CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <p className="font-semibold text-sm">{t.title}</p>
                    <div className="flex gap-1 flex-shrink-0">
                      {t.maturity && <Badge className={`text-[10px] border-0 ${MATURITY_COLORS[t.maturity] || ""}`}>{t.maturity}</Badge>}
                      {t.impact && <Badge className={`text-[10px] border-0 ${IMPACT_COLORS[t.impact] || ""}`}>impacto {t.impact}</Badge>}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{t.summary}</p>
                </CardContent></Card>
              ))}
            </div>
          </div>
        )}

        {(st.technologies || []).length > 0 && (
          <div>
            <h2 className="font-bold text-base mb-3 flex items-center gap-2"><FlaskConical className="w-4 h-4 text-violet-500" /> Tecnologías clave</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
              {st.technologies.map((t, i) => <TechCard key={i} t={t} index={i} />)}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          {(st.experts || []).length > 0 && (
            <Card><CardContent className="p-5">
              <h2 className="font-bold text-base mb-3 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-sky-500" /> Expertos</h2>
              <div className="space-y-3">
                {st.experts.map((e, i) => (
                  <div key={i} className="text-sm">
                    <div className="font-semibold">{e.name} <span className="text-xs font-normal text-muted-foreground">· {e.affiliation}</span></div>
                    <p className="text-xs text-muted-foreground">{e.why}</p>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          )}
          {(st.organizations || []).length > 0 && (
            <Card><CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-base flex items-center gap-2"><Building2 className="w-4 h-4 text-emerald-600" /> Organizaciones</h2>
                <Button size="sm" variant="outline" onClick={addOrgsToEcosystem} disabled={savingEco} data-testid="research-add-ecosystem">
                  {savingEco ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Network className="w-3.5 h-3.5 mr-1" />}
                  Añadir al Ecosistema
                </Button>
              </div>
              <div className="space-y-3">
                {st.organizations.map((o, i) => (
                  <div key={i} className="text-sm">
                    <div className="font-semibold flex items-center gap-1.5 flex-wrap">{o.name} <Badge variant="outline" className="text-[10px]">{o.type}</Badge> <span className="text-xs font-normal text-muted-foreground">{o.country}</span></div>
                    <p className="text-xs text-muted-foreground">{o.description}</p>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          )}
        </div>

        {(st.opportunities || []).length > 0 && (
          <Card className="border-emerald-500/30"><CardContent className="p-5">
            <h2 className="font-bold text-base mb-3 flex items-center gap-2"><Rocket className="w-4 h-4 text-emerald-600" /> Oportunidades para Valoriza</h2>
            <ul className="space-y-2">
              {st.opportunities.map((o, i) => (
                <li key={i} className="text-sm flex gap-2"><span className="text-emerald-600 font-bold">{i + 1}.</span> {o}</li>
              ))}
            </ul>
          </CardContent></Card>
        )}

        <Tabs defaultValue="landscape">
          <TabsList>
            <TabsTrigger value="landscape" data-testid="research-tab-landscape">Informe de Paisaje</TabsTrigger>
            <TabsTrigger value="technology" data-testid="research-tab-technology">Informe Tecnológico</TabsTrigger>
          </TabsList>
          <TabsContent value="landscape">
            {(() => {
              const { markdown, references } = extractCitations(selected.landscape_md || "Sin contenido");
              return (
                <Card><CardContent className="p-6">
                  <ReactMarkdown components={md}>{markdown}</ReactMarkdown>
                  <ReferenceList references={references} />
                </CardContent></Card>
              );
            })()}
          </TabsContent>
          <TabsContent value="technology">
            {(() => {
              const { markdown, references } = extractCitations(selected.technology_md || "Sin contenido");
              return (
                <Card><CardContent className="p-6">
                  <ReactMarkdown components={md}>{markdown}</ReactMarkdown>
                  <ReferenceList references={references} />
                </CardContent></Card>
              );
            })()}
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // ---------- Vista lista ----------
  return (
    <div className="space-y-6" data-testid="research-page">
      <div className="rounded-2xl p-6 md:p-8 text-white relative overflow-hidden" style={{ background: "linear-gradient(120deg, #1e1b4b, #4338ca 70%, #6d28d9)" }}>
        <div className="flex items-center gap-2 text-indigo-200 text-xs font-semibold uppercase tracking-widest mb-2">
          <Microscope className="w-4 h-4" /> Investigación autónoma con IA
        </div>
        <h1 className="text-3xl md:text-4xl font-black">Deep Research</h1>
        <p className="text-indigo-100/90 mt-2 max-w-2xl text-sm">
          Lanza un tema y los agentes IA generan en segundo plano un <strong>Informe de Paisaje</strong> y un <strong>Informe Tecnológico</strong> con
          tendencias, expertos y organizaciones reales. Los informes quedan disponibles para toda la organización.
        </p>
        <div className="mt-5 flex flex-col md:flex-row gap-2 max-w-3xl">
          <Input
            value={topic} onChange={(e) => setTopic(e.target.value)}
            placeholder='Tema · ej: "Hidrógeno verde en gestión de residuos"'
            className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
            data-testid="research-topic-input"
          />
          <Input
            value={focus} onChange={(e) => setFocus(e.target.value)}
            placeholder="Enfoque opcional"
            className="bg-white/10 border-white/20 text-white placeholder:text-white/50 md:w-56"
            data-testid="research-focus-input"
          />
          <Button onClick={launch} disabled={launching} className="bg-white text-indigo-900 hover:bg-indigo-100 font-bold" data-testid="research-launch-btn">
            {launching ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Investigar
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {reports.map((r) => {
          const running = !["done", "error"].includes(r.status);
          return (
            <Card
              key={r.id}
              className={`cursor-pointer transition-all hover:shadow-md ${running ? "border-indigo-500/40" : ""}`}
              onClick={() => r.status === "done" && openReport(r.id)}
              data-testid={`research-card-${r.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-bold text-sm truncate">{r.topic}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{r.created_by} · {new Date(r.created_at).toLocaleDateString("es-ES")}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {r.status === "done" && <Badge className="bg-emerald-500/15 text-emerald-600 border-0 text-[10px]">Completado</Badge>}
                    {r.status === "error" && <Badge variant="destructive" className="text-[10px]">Error</Badge>}
                    {running && <Badge className="bg-indigo-500/15 text-indigo-600 border-0 text-[10px] animate-pulse">En curso</Badge>}
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => remove(r.id, e)}>
                      <Trash2 className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
                    </Button>
                  </div>
                </div>
                {running && (
                  <div className="mt-3 space-y-2">
                    <Progress value={r.progress || 0} className="h-1.5" />
                    <div className="flex items-center gap-2 text-xs text-indigo-600">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> {r.status_label}
                    </div>
                    <div className="flex gap-1.5">
                      {STEPS.map((s, i) => {
                        const stepIdx = STEPS.findIndex((x) => x.key === r.status);
                        const done = stepIdx > i || r.status === "done";
                        const active = s.key === r.status;
                        return (
                          <span key={s.key} className={`text-[10px] px-2 py-0.5 rounded-full ${done ? "bg-emerald-500/15 text-emerald-600" : active ? "bg-indigo-500/15 text-indigo-600" : "bg-muted text-muted-foreground"}`}>
                            {s.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
                {r.status === "error" && <p className="text-xs text-destructive mt-2">{r.status_label}</p>}
              </CardContent>
            </Card>
          );
        })}
        {reports.length === 0 && (
          <div className="md:col-span-2 text-center py-12 text-sm text-muted-foreground">
            Aún no hay investigaciones. Lanza la primera desde arriba 🔬
          </div>
        )}
      </div>
    </div>
  );
}
